import { Injectable } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { clientDetection } from '../globalFunctions/globalFunctions';
import { RegistryApiService } from './registry-api.service';

@Injectable({ providedIn: 'root' })
export class SmartLabelService {
    private activeDictionary: Record<string, string> = {};
    private loadActiveDictionaryPromise: Promise<void> | undefined;

    constructor(private readonly registryApiService: RegistryApiService) { }

    /**
     * Get the label text from the active dictionary via its key as an observable.
     * Overload signatures describe how the return type changes based on whether a fallback is supplied:
     * no fallback (or null) can return undefined, while a string fallback guarantees a string result.
     */
    public getLabelText(key: string): Observable<string | undefined>;
    public getLabelText(
        key: string,
        options: { replacements?: Record<string, string | undefined>; fallback?: null },
    ): Observable<string | undefined>;
    public getLabelText(
        key: string,
        options: { replacements?: Record<string, string | undefined>; fallback: string },
    ): Observable<string>;
    public getLabelText(
        key: string,
        options?: { replacements?: Record<string, string | undefined>, fallback?: string | null }
    ): Observable<string | undefined> {
        return from(this.loadActiveDictionary()).pipe(
            map(() => {
                const normalizedKey: string = this.normalizeKey(key);
                const labelText: string | undefined = this.activeDictionary[normalizedKey];
                const resolvedText: string | undefined = labelText ?? options?.fallback ?? undefined;

                const replacedText: string | undefined = resolvedText === undefined
                    ? undefined
                    : this.applyReplacements(resolvedText, options?.replacements).trim();

                // Highlight this label with decorations if the key was not in the dictionary
                return Object.hasOwn(this.activeDictionary, normalizedKey)
                    ? replacedText
                    : this.highlightMissingKey(key, replacedText);
            }),
        );
    }

    // This is developer functionality to highlight in the UI that a smartlabel key is missing
    private readonly highlightMissingKey = (key: string, text?: string): string | undefined => {
        // This is not something to show on a production environment
        if (environment.production) {
            return text;
        }

        // Change the returning text with a bit of flavor
        return (text ?? '').length > 0
            ? `<< MISSING SMARTLABEL: ${key} >> ${text}`
            : `<< MISSING SMARTLABEL: ${key} >>`;
    };

    /** Normalizes the key values for the dictionary get/set since they are case sensitive. */
    private readonly normalizeKey = (key: string): string => key.trim().toLowerCase();

    /** Loads the active dictionary and guards from fetching multiple times per page. */
    private readonly loadActiveDictionary = async (): Promise<void> => {
        // Do not fetch the base and client dictionaries if it is loading or has already loaded
        if (this.loadActiveDictionaryPromise !== undefined) {
            await this.loadActiveDictionaryPromise;
            return;
        }

        // Fetch the base and client dictionaries and set the promise to have a non-undefined value
        this.loadActiveDictionaryPromise = this.loadCombinedDictionaries();
        await this.loadActiveDictionaryPromise;
    };

    /** Loads both the base and client dictionaries to the active dictionary. */
    private readonly loadCombinedDictionaries = async (): Promise<void> => {
        const locale: string = environment.smartLabelsDefaultLocale;
        const client: string = clientDetection();

        // Read the dictionaries from both the base and client paths
        const baseDictionaryPath: string = `${environment.smartLabelsBasePath}/${locale}/base.json`;
        const clientDictionaryPath: string = `${environment.smartLabelsBasePath}/${locale}/clients/${client}.json`;

        const [baseDictionary, clientDictionary] = await Promise.all([
            this.readDictionary(baseDictionaryPath),
            this.readDictionary(clientDictionaryPath),
        ]);

        // Set the active dictionary as the base dictionary with the client dictionary acting as an override
        this.activeDictionary = {
            ...baseDictionary,
            ...clientDictionary,
        };
    };

    /** Fetches the dictionary at the provided path and returns a normalized dictionary. */
    private readonly readDictionary = async (path: string): Promise<Record<string, string>> => {
        try {
            // Try to get a response from the supplied path - return an empty dictionary if response is not "ok"
            const response: Response = await fetch(path);
            const dictionary: Record<string, string> = response.ok ? await response.json() : {};

            // Normalize the dictionary since keys are case sensitive when getting a value from a given key
            const normalizedDictionary: Record<string, string> = {};

            for (const [key, value] of Object.entries(dictionary)) {
                normalizedDictionary[this.normalizeKey(key)] = value;
            }

            return normalizedDictionary;
        } catch {
            // Return an empty dictionary in the case of an error during the fetch
            return {};
        }
    };

    /** Apply any provided replacements to existing tokens within the provided text. */
    private readonly applyReplacements = (text: string, replacements?: Record<string, string | undefined>): string => {
        // Return the unmodified text early if there are no replacements provided
        if (!replacements || Object.keys(replacements).length === 0) {
            return text;
        }

        // Look for any replacement tokens within the provided text and replace where applicable
        let result: string = text;

        for (const [key, value] of Object.entries(replacements)) {
            if (value === undefined || value === null) {
                continue;
            }

            const token = new RegExp(String.raw`\[${key}\]`, 'g');
            result = result.replace(token, value.trim());
        }

        return result;
    };
}
